import React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'icon';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface MPButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: [
    'mp-gradient-orange',
    'mp-tw-text-white mp-tw-font-semibold',
    'mp-tw-shadow-mp-orange',
    'hover:mp-tw-shadow-mp-orange-hover hover:mp-tw--translate-y-px',
    'active:mp-tw-translate-y-0',
    'disabled:mp-tw-opacity-60 disabled:mp-tw-cursor-not-allowed disabled:mp-tw-transform-none disabled:mp-tw-shadow-none',
  ].join(' '),
  secondary: [
    'mp-tw-bg-mp-button mp-tw-text-mp-text',
    'mp-tw-border mp-tw-border-mp-border',
    'mp-tw-rounded-mp-lg',
    'hover:mp-tw-border-mp-orange hover:mp-tw-text-mp-orange hover:mp-tw--translate-y-px',
    'active:mp-tw-translate-y-0',
  ].join(' '),
  ghost: [
    'mp-tw-bg-transparent mp-tw-text-mp-text',
    'mp-tw-border-none',
    'hover:mp-tw-bg-mp-orange-ultra-light hover:mp-tw-text-mp-orange',
  ].join(' '),
  danger: [
    'mp-tw-bg-mp-error mp-tw-text-white mp-tw-font-semibold',
    'mp-tw-border-none',
    'hover:mp-tw-opacity-90 hover:mp-tw--translate-y-px',
  ].join(' '),
  icon: [
    'mp-tw-bg-transparent mp-tw-text-mp-text-muted',
    'mp-tw-border mp-tw-border-mp-border mp-tw-rounded-mp-lg',
    'mp-tw-p-0 mp-tw-flex mp-tw-items-center mp-tw-justify-center',
    'hover:mp-tw-bg-mp-orange-ultra-light hover:mp-tw-text-mp-orange hover:mp-tw-border-mp-orange-light',
    'focus:mp-focus-ring',
  ].join(' '),
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'mp-tw-px-3 mp-tw-py-1.5 mp-tw-text-mp-sm mp-tw-rounded-mp-md',
  md: 'mp-tw-px-5 mp-tw-py-2.5 mp-tw-text-mp-base mp-tw-rounded-mp-lg',
  lg: 'mp-tw-px-7 mp-tw-py-3 mp-tw-text-mp-md mp-tw-rounded-mp-xl',
};

const iconSizeClasses: Record<ButtonSize, string> = {
  sm: 'mp-tw-w-8 mp-tw-h-8',
  md: 'mp-tw-w-10 mp-tw-h-10',
  lg: 'mp-tw-w-12 mp-tw-h-12',
};

const MPButton: React.FC<MPButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  children,
  className = '',
  disabled,
  ...rest
}) => {
  const isIcon = variant === 'icon';
  const baseClasses = [
    'mp-tw-inline-flex mp-tw-items-center mp-tw-justify-center mp-tw-gap-2',
    'mp-tw-border-none mp-tw-cursor-pointer',
    'mp-tw-transition-all mp-tw-duration-mp-normal mp-tw-ease-mp-ease',
    'focus:mp-tw-outline-none focus:mp-focus-ring',
    variantClasses[variant],
    isIcon ? iconSizeClasses[size] : sizeClasses[size],
    fullWidth ? 'mp-tw-w-full' : '',
    loading ? 'mp-tw-opacity-70 mp-tw-cursor-wait' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      className={baseClasses}
      disabled={disabled || loading}
      aria-busy={loading}
      {...rest}
    >
      {loading && (
        <svg
          className="mp-tw-animate-spin mp-tw-h-4 mp-tw-w-4"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            className="mp-tw-opacity-25"
            cx="12" cy="12" r="10"
            stroke="currentColor" strokeWidth="4"
          />
          <path
            className="mp-tw-opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      )}
      {children}
    </button>
  );
};

export default MPButton;
